import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { DesignPageIntro } from '@shared/designVariants/DesignPageIntro';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Typography,
} from '@mui/material';

import { CampaignList } from './CampaignList';
import PageContainer from '@shared/components/container/PageContainer';
import { useWelcomeToast } from '@features/Auth/Welcome';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';

import { useStyles } from './styles';
import { FilterCard } from '@shared/components/filterCard/FilterCard';
import { ResponseMsg } from '@shared/types/admin/agent';
import CustomAlert from '@shared/components/alert/alert';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CompanyBillingStatus } from '@features/Admin/Billing/ui/CompanyBillingStatus';

export const CampaignPage: React.FC = () => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  useWelcomeToast();
  const [creating, setCreating] = useState(false);
  const role = useAppSelector((state) => state.auth.authenticatedUser?.role);
  const [responseMsg, setResponseMsg] = useState<ResponseMsg>({
    message: '',
    type: 'success',
  });

  const currentCompany = useAppSelector((state) => state.auth.authenticatedUserCompany);

  useEffect(() => {
    if (responseMsg.message) {
      const timer = setTimeout(() => {
        setResponseMsg({ message: '', type: 'success' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [responseMsg]);

  useEffect(() => {
    dispatch(actions.customizer.toggleLayout('full'));
  }, [dispatch]);

  const setLoading = async (_flag: boolean) => {};

  const onCampaignDeleteError = (error: ResponseMsg) => {
    setResponseMsg(error);
  };

  return (
    <>
      <PageContainer
        title={t('adminMainPage.menuItems.campaignList')}
        companyName={currentCompany?.title}
        description={t('adminMainPage.menuItems.campaignList')}>
        {/* <Breadcrumb
          title={t('adminMainPage.header.companyName', { companyName: currentCompany?.title })}
          items={BCrumb}>
          <></>
        </Breadcrumb> */}
        <Box className={classes.main}>
          <DesignPageIntro
            area="campaigns"
            action={
              <Button
                variant="contained"
                startIcon={<IconPlus size={17} />}
                disabled={role === UserRolesEnum.VIEWER}
                onClick={() =>
                  variant === 'main'
                    ? setCreating(true)
                    : workspace.navigate('/workspace/campaign/new')
                }>
                {variant === 'v2' ? 'Add assistant' : 'New campaign'}
              </Button>
            }
          />
          {responseMsg.message ? (
            <CustomAlert message={responseMsg.message} type={responseMsg.type} />
          ) : (
            ''
          )}
          <CompanyBillingStatus />
          {variant === 'main' ? (
            <FilterCard />
          ) : (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
              }}>
              <AccordionSummary expandIcon={<IconChevronDown size={17} />}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Company usage
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Usage, capacity and campaign overview
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FilterCard />
              </AccordionDetails>
            </Accordion>
          )}
          <CampaignList
            setLoading={setLoading}
            onCampaignDeleteError={onCampaignDeleteError}
            createOpen={creating}
            onCreateChange={setCreating}
          />
        </Box>
      </PageContainer>
    </>
  );
};
